import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { IdentityDocument } from '@mews-commander/data-access';
import { WallClockDateService } from '@mews/framework';
import { IdentityDocumentType } from '@mews-commander/legacy-data-structures';
import { localizeWallClockDateString, mapId } from '@mews-commander/utils';
import {
    getIdentityDocumentName,
    getIdentityDocumentsByOwnerId,
    getIdentityDocumentsByIds,
    getIdentityDocumentScans,
} from '@mews-commander/legacy-entities/src/ducks/identityDocuments';
import { getSelectedEntityId } from '../entityManagement/selectors';
import { sortByCreatedUtcDescending } from '../entityManagement/utils';

export const getOwnerSortedIdentityDocumentsIds = flow(
    getIdentityDocumentsByOwnerId,
    sortByCreatedUtcDescending,
    mapId
);

export const getSelectedIdentityDocumentsByIds = createSelector(getIdentityDocumentsByIds, (documents) => {
    const today = WallClockDateService.today();
    return pipe(
        documents,
        array.map((d: IdentityDocument) => {
            const { expiryDateUtc, issueDateUtc, type, ...other } = d;
            const documentType = type && IdentityDocumentType.getByName(type);
            const expiryDate = d.expiryDateUtc && WallClockDateService.parse(d.expiryDateUtc);
            return {
                issueDate: d.issueDateUtc && WallClockDateService.parse(d.issueDateUtc),
                expiryDate,
                isExpired: expiryDate && WallClockDateService.isLess(expiryDate, today),
                type: documentType,
                ...other,
            };
        }),
        sortByCreatedUtcDescending
    );
});

export const getSelectedIdentityDocument = createSelector(
    getIdentityDocumentsByOwnerId,
    getSelectedEntityId,
    (documents, id) =>
        pipe(
            documents,
            array.findFirst((document) => document.id === id),
            option.toNullable
        )
);

export const getSelectedIdentityDocumentScans = flow(getSelectedIdentityDocument, (doc) => {
    if (doc == null) {
        return [];
    }
    return getIdentityDocumentScans(doc);
});

export const getSelectedIdentityDocumentFormValues = flow(getSelectedIdentityDocument, (doc) => {
    if (doc == null) {
        return null;
    }

    const { issueDateUtc, expiryDateUtc } = doc;

    return {
        type: doc.type ?? null,
        number: doc.number ?? null,
        createdUtc: doc.createdUtc ?? null,
        issuingCountryCode: doc.issuingCountryCode ?? null,
        issuingCity: doc.issuingCity ?? null,
        issueDateString: localizeWallClockDateString(issueDateUtc) ?? null,
        expiryDateString: localizeWallClockDateString(expiryDateUtc) ?? null,
        documentSupportNumber: doc.documentSupportNumber ?? null,
        isVerified: doc.isVerified ?? false,
        scans: doc.scans ?? [],
    };
});

export const getSelectedIdentityDocumentName = createSelector(getSelectedIdentityDocument, (doc) =>
    doc === null ? '' : getIdentityDocumentName(doc)
);
